/**
 * Homepage publique — Moteur de recherche jurisprudentiel
 * Route : /
 * SEO : Helmet OG + Twitter Card + JSON-LD WebSite+SearchAction
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { LoadingQuiz } from '@/components/jurisprudence/LoadingQuiz';
import {
  PublicHeader, PublicFooter, FicheCard, MCPBadge,
  EMPTY_ADV_FILTERS,
  type PublicFiche, type AdvancedFilters,
} from '@/components/jurisprudence';
import { SmartSearchBar } from '@/components/jurisprudence/SmartSearch';
import { QuickFilters, applyQuickFilters } from '@/components/jurisprudence/QuickFilters';
import { StatsSection } from '@/components/jurisprudence/StatsSection';
import { PitchSection } from '@/components/jurisprudence/PitchSection';
import { FicheSection } from '@/components/jurisprudence/FicheSection';
import { MCPSection } from '@/components/jurisprudence/MCPSection';
import { FAQSection } from '@/components/jurisprudence/FAQSection';
import { AboutSection } from '@/components/jurisprudence/AboutSection';
import { DomainesRecentesSection } from '@/components/jurisprudence/DomainesRecentesSection';
import { SubscriptionSection } from '@/components/jurisprudence/SubscriptionSection';
import { TestimonialsSection } from '@/components/jurisprudence/TestimonialSection';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { useStats } from '@/hooks/useStats';
import { motion } from 'framer-motion';
import { ParallaxSlide, useHeroParallax } from '@/components/jurisprudence/Parallax';
import { DesktopPromoSection } from '@/components/jurisprudence/DesktopPromoSection';
import { isDesktop } from '@/AppMode';

const SITE_URL = 'https://smartlawyer.ai';
const OG_IMAGE = `${SITE_URL}/api/fiches/og-image/default.png`;

const _DOMAINE_ICONS: Record<string, string> = {
  'droit social':        '👷',
  'droit civil':         '⚖️',
  'droit commercial':    '🏢',
  'droit pénal':         '🔒',
  'procédure civile':    '📋',
  'droit fiscal':        '💶',
  'droit administratif': '🏛️',
};

// ── Page principale ───────────────────────────────────────────────────────────

export default function PublicJurisprudence() {
  const { t } = useTranslation();
  const locale = getDateLocale();

  const [query, setQuery]               = useState('');
  const [searching, setSearching]       = useState(false);
  const [results, setResults]           = useState<PublicFiche[]>([]);
  const [searchDone, setSearchDone]     = useState(false);
  const [searchTime, setSearchTime]     = useState<number | null>(null);
  const [recent, setRecent]             = useState<PublicFiche[]>([]);
  const [_loadingInit, setLoadingInit]   = useState(true);

  const [launchedQuery, setLaunchedQuery]     = useState<string | null>(null);
  const [quickFilters, setQuickFilters]       = useState<Record<string, string>>({});
  const resultsRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const hero = useHeroParallax(heroRef);
  const [allResults, setAllResults]     = useState<PublicFiche[]>([]);
  const [displayCount, setDisplayCount] = useState(0);
  const DISPLAY_STEP = 10;
  const currentFiltersRef = useRef<AdvancedFilters | undefined>();

  const { user }            = usePublicAuth();
  const { stats } = useStats();

const totalStrDisplay = stats ? stats.total.toLocaleString(locale) : `${(1000000).toLocaleString(locale)}+`;

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    const saved = window.history.state as {
      query?: string;
      allResults?: PublicFiche[];
      displayCount?: number;
      searchTime?: number;
      searchDone?: boolean;
    } | null;

    if (saved?.allResults?.length) {
      setQuery(saved.query || '');
      setAllResults(saved.allResults);
      setResults(saved.allResults.slice(0, saved.displayCount || 10));
      setDisplayCount(saved.displayCount || 10);
      setSearchTime(saved.searchTime ?? null);
      setSearchDone(true);
      setLoadingInit(false);
      return;
    }

    const sharedQ = new URLSearchParams(window.location.search).get('q');

    fetch('/api/fiches/browse-sorted?limit=6')
      .then(r => r.json())
      .then(f => { setRecent(f.results || []); })
      .catch(console.error)
      .finally(() => {
        setLoadingInit(false);
        if (sharedQ) {
          setQuery(sharedQ);
          doSearchRef.current?.(sharedQ, undefined, true);
        }
      });
  }, []);

  const doSearchRef = useRef<(q: string, filters?: AdvancedFilters, skipScroll?: boolean) => Promise<void>>();

  const buildFiltersBody = (filters?: AdvancedFilters): Record<string, string> => {
    if (!filters) return {};
    const f: Record<string, string> = {};
    if (filters.jurisdiction) f.jurisdiction = filters.jurisdiction;
    if (filters.domaine)      f.domaine      = filters.domaine;
    if (filters.solution)     f.solution     = filters.solution;
    if (filters.chamber)      f.chamber      = filters.chamber;
    if (filters.date_from)    f.date_from    = filters.date_from;
    if (filters.date_to)      f.date_to      = filters.date_to;
    return f;
  };

  const doSearch = async (q: string, filters?: AdvancedFilters, skipScroll = false) => {
    if (!q.trim()) return;
    setSearching(true); setSearchDone(false); setSearchTime(null);
    setAllResults([]); setResults([]); setDisplayCount(0); setQuickFilters({});
    currentFiltersRef.current = filters;
    const t0 = performance.now();
    try {
      const f    = buildFiltersBody(filters);
      const body: Record<string, unknown> = { query: q, limit: 100 };
      if (Object.keys(f).length) body.filters = f;

      const res = await fetch('/api/fiches/search', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.email ? { 'X-User-Email': user.email, 'X-User-Id': user.uid } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      const fetched: PublicFiche[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      const elapsed = Math.round(performance.now() - t0);
      setSearchTime(elapsed);
      setSearchDone(true);
      window.history.replaceState(
        { query: q, allResults: fetched, displayCount: DISPLAY_STEP, searchTime: elapsed, searchDone: true },
        '',
        `/?q=${encodeURIComponent(q)}`,
      );
      if (!skipScroll) {
        setTimeout(() => {
          if (resultsRef.current) {
            const top = resultsRef.current.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        }, 150);
      }
    } catch (e) { console.error('doSearch error:', e); }
    finally { setSearching(false); }
  };

  const loadMore = () => {
    setDisplayCount(prev => {
      const next = prev + DISPLAY_STEP;
      setResults(allResults.slice(0, next));
      const cur = window.history.state || {};
      window.history.replaceState({ ...cur, displayCount: next }, '');
      return next;
    });
  };

  doSearchRef.current = doSearch;

  const _handleSubmit = (e: React.FormEvent) => { e.preventDefault(); doSearch(query); };

  const handleReset = () => {
    setQuery(''); setResults([]); setAllResults([]); setSearchDone(false);
    setSearchTime(null); setDisplayCount(0); currentFiltersRef.current = undefined;
    setQuickFilters({});
    window.history.replaceState(null, '', '/');
    setLaunchedQuery(null);
  };

  const handleSmartSearch = useCallback((q: string, filters?: Record<string, string>) => {
    setQuery(q);
    // Convertir les filtres génériques en AdvancedFilters
    const advFilters: AdvancedFilters = {
      ...EMPTY_ADV_FILTERS,
      domaine:      filters?.domaine      ?? '',
      solution:     filters?.solution     ?? '',
      date_from:    filters?.date_from    ?? '',
      date_to:      filters?.date_to      ?? '',
      jurisdiction: filters?.jurisdiction ?? '',
      chamber:      filters?.chamber      ?? '',
      enriched_only: false,
      quality_min: '',
    };
    doSearchRef.current?.(q, advFilters);
  }, []);

  const _handleAdvApply = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    window.location.href = `/browse?${params}`;
  };

  const isHome = !searchDone && !searching;

  const pageTitle = searchDone && query
    ? t('jurisprudence.meta.title_search', { query })
    : t('jurisprudence.meta.title_home');

  const pageDescription = searchDone && query
    ? t('jurisprudence.meta.description_search', { n: allResults.length, query, total: totalStrDisplay })
    : t('jurisprudence.meta.description_home', { total: totalStrDisplay });

  const canonicalUrl = searchDone && query
    ? `${SITE_URL}/?q=${encodeURIComponent(query)}`
    : `${SITE_URL}/`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type"         content="website" />
        <meta property="og:site_name"    content="SmartLawyer" />
        <meta property="og:title"        content={pageTitle} />
        <meta property="og:description"  content={pageDescription} />
        <meta property="og:url"          content={canonicalUrl} />
        <meta property="og:image"        content={OG_IMAGE} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="628" />
        <meta property="og:image:type"   content="image/jpeg" />
        <meta property="og:locale"       content={locale.replace('-', '_')} />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@smartlawyer_ai" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image"       content={OG_IMAGE} />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type':  'WebSite',
              '@id':    `${SITE_URL}/#website`,
              name:     'SmartLawyer',
              url:      SITE_URL,
              description: t('jurisprudence.meta.jsonld_website', { total: totalStrDisplay }),
              inLanguage:  locale,
              publisher: {
                '@type': 'Organization',
                '@id':   `${SITE_URL}/#organization`,
                name:    'SmartLawyer',
                url:     SITE_URL,
                logo: {
                  '@type':    'ImageObject',
                  url:        `${SITE_URL}/public/logo_dark.png`,
                  contentUrl: `${SITE_URL}/public/logo_dark.png`,
                },
              },
              potentialAction: {
                '@type':       'SearchAction',
                target:        `${SITE_URL}/?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type':      'Organization',
              '@id':        `${SITE_URL}/#organization`,
              name:         'SmartLawyer',
              url:          SITE_URL,
              description:  t('jurisprudence.meta.jsonld_org'),
              foundingDate: '2024',
              inLanguage:   locale,
              areaServed:   'France',
              serviceType:  'Legal Research',
            },
          ],
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader onLogoClick={handleReset} />

        <main className="flex-1">

          <section ref={heroRef} className={`transition-all duration-500 relative overflow-hidden ${isHome ? 'flex flex-col justify-center min-h-[calc(100svh-80px)] py-10 sm:py-14' : 'py-8 border-b bg-muted/30'}`}>

            {/* ── Filigrane : logo animé (comme dans le menu), en parallaxe ── */}
            {isHome && (
              // Fondu en bas sur un conteneur fixe : pas de ligne de coupe
              // quand le logo descend avec la parallaxe
              <div
                className="absolute inset-0 pointer-events-none select-none"
                style={{
                  maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)'
                }}
                aria-hidden="true"
              >
                <motion.div
                  className="absolute inset-0 flex items-center justify-center sm:justify-end"
                  style={{ y: hero.watermarkY, scale: hero.watermarkScale }}
                >
                  <img
                    src="/public/smartlawyer_animated.svg"
                    alt=""
                    className="w-[560px] h-[560px] sm:w-[820px] sm:h-[820px] sm:-mr-24 shrink-0"
                    style={{ opacity: 0.06 }}
                  />
                </motion.div>
              </div>
            )}

            <motion.div
              className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6"
              style={isHome ? { y: hero.contentY, opacity: hero.contentOpacity } : undefined}
            >

              {/* ── Eyebrow + H1 (home uniquement) ── */}
              {isHome && (
                <div className="text-center mb-8 max-w-4xl mx-auto">
                  <span className="block text-[11px] font-bold uppercase tracking-[.12em] text-muted-foreground mb-5">
                    {t('jurisprudence.home.eyebrow')}
                  </span>
                  <h1 className="font-serif text-[38px] sm:text-[48px] font-bold leading-[1.1] tracking-[-0.04em] text-foreground mb-0">
                    {t('jurisprudence.home.hero_title_1')}<br />
                    <em className="not-italic text-primary">{t('jurisprudence.home.hero_title_2')}</em>
                  </h1>
                </div>
              )}

              {/* ── sw-wrap : toprow + toggle + recherche ── */}
              <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-3">

                {/* Toprow : compteur (home uniquement) */}
                {isHome && (
                  <>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <p className="text-[13px] font-bold text-foreground">
                      <span className="text-lg leading-none">⚖</span>{' '}
                      <Trans
                        i18nKey="jurisprudence.home.indexed_count"
                        values={{ total: stats ? stats.total.toLocaleString(locale) : (1168412).toLocaleString(locale) }}
                        components={[<strong key="0" className="text-primary" />]}
                      />
                    </p>
                    <Link
                      to="/browse"
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                    >
                      {t('search.advanced_search')}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </Link>
                  </div>
                  <div className="h-px bg-border/60 mt-3" />
                  </>
                )}

                <SmartSearchBar
                  onSmartSearch={handleSmartSearch}
                  searching={searching}
                  launchedQuery={launchedQuery}
                  context="jurisprudence"
                />
              </div>

              {/* ── Corpus spécialisés ── */}
              {isHome && (
                <div className="mt-6">
                  <p className="text-center text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/50 mb-3">
                    {t('jurisprudence.home.corpus_title')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2.5">
                    <Link to="/jurisprudence/principes"
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-200 bg-amber-50/60 dark:bg-amber-900/10 dark:border-amber-800 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all shadow-sm">
                      <span className="text-base leading-none">⭐</span>
                      <span className="flex flex-col">
                        <span className="text-[10px] font-bold tracking-wide uppercase text-amber-600 dark:text-amber-400 leading-none">{t('jurisprudence.home.corpus_label')}</span>
                        <span className="text-[12px] font-medium text-foreground leading-tight">{t('jurisprudence.home.corpus_principes')}</span>
                      </span>
                    </Link>
                    <Link to="/jurisprudence/rgpd"
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-blue-200 bg-blue-50/60 dark:bg-blue-900/10 dark:border-blue-800 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all shadow-sm">
                      <span className="text-base leading-none">🔒</span>
                      <span className="flex flex-col">
                        <span className="text-[10px] font-bold tracking-wide uppercase text-blue-600 dark:text-blue-400 leading-none">{t('jurisprudence.home.corpus_label')}</span>
                        <span className="text-[12px] font-medium text-foreground leading-tight">{t('jurisprudence.home.corpus_rgpd')}</span>
                      </span>
                    </Link>
                    <Link to="/jurisprudence/cjue"
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-900/10 dark:border-indigo-800 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all shadow-sm">
                      <span className="text-base leading-none">🇪🇺</span>
                      <span className="flex flex-col">
                        <span className="text-[10px] font-bold tracking-wide uppercase text-indigo-600 dark:text-indigo-400 leading-none">{t('jurisprudence.home.corpus_label')}</span>
                        <span className="text-[12px] font-medium text-foreground leading-tight">{t('jurisprudence.home.corpus_cjue')}</span>
                      </span>
                    </Link>
                    <Link to="/reglements"
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-violet-200 bg-violet-50/60 dark:bg-violet-900/10 dark:border-violet-800 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all shadow-sm">
                      <span className="text-base leading-none">📋</span>
                      <span className="flex flex-col">
                        <span className="text-[10px] font-bold tracking-wide uppercase text-violet-600 dark:text-violet-400 leading-none">{t('jurisprudence.home.corpus_reglements_label')}</span>
                        <span className="text-[12px] font-medium text-foreground leading-tight">{t('jurisprudence.home.corpus_reglements')}</span>
                      </span>
                    </Link>
                  </div>
                </div>
              )}

              {/* ── Hero under : créer compte | s'abonner ── */}
              {isHome && (
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-6 text-[12px]">
                  <Link to="/login" className="text-primary font-semibold hover:underline">
                    {t('jurisprudence.home.create_account')}
                  </Link>
                  <span className="text-muted-foreground">{t('jurisprudence.home.searches_per_day')}</span>
                  <span className="w-px h-3.5 bg-border hidden sm:block" />
                  <span className="text-muted-foreground hidden sm:inline">
                    {t('jurisprudence.home.unlimited')}
                  </span>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700 transition-colors shadow-sm"
                  >
                    {t('jurisprudence.home.subscribe_cta')}
                  </Link>
                </div>
              )}

              {/* ── SmartLawyer Desktop : pastille de téléchargement ── */}
              {isHome && !isDesktop() && (
                <div className="mt-6 flex justify-center">
                  <Link
                    to="/desktop"
                    className="group inline-flex items-center gap-2 rounded-full border border-violet-200 dark:border-violet-800 bg-white/80 dark:bg-violet-950/40 backdrop-blur px-1.5 py-1.5 pr-4 text-sm shadow-sm hover:border-violet-400 transition-colors"
                  >
                    <span className="rounded-full bg-violet-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      {t('desktop.promo.badge')}
                    </span>
                    <span aria-hidden="true">🖥️</span>
                    <span className="font-semibold text-foreground">{t('desktop.promo.title')}</span>
                    <span className="text-violet-600 dark:text-violet-400 group-hover:translate-x-0.5 transition-transform">
                      {t('desktop.promo.cta')} →
                    </span>
                  </Link>
                </div>
              )}

            </motion.div>
          </section>
          {!searching && !searchDone && <ParallaxSlide><StatsSection stats={stats} /></ParallaxSlide>}
          {!searching && !searchDone && <ParallaxSlide><PitchSection /></ParallaxSlide>}
          {!searching && !searchDone && !isDesktop() && (
            <ParallaxSlide><DesktopPromoSection /></ParallaxSlide>
          )}
          {!searching && !searchDone && (
            <ParallaxSlide><DomainesRecentesSection stats={stats} recent={recent} /></ParallaxSlide>
          )}
          {!searching && !searchDone && <ParallaxSlide><FAQSection /></ParallaxSlide>}
          {!searching && !searchDone && (
            <ParallaxSlide><FicheSection onSearch={() => window.scrollTo({ top: 0, behavior: 'smooth' })} /></ParallaxSlide>
          )}
          {!searching && !searchDone && <ParallaxSlide><MCPSection /></ParallaxSlide>}
          {!searching && !searchDone && <ParallaxSlide><TestimonialsSection /></ParallaxSlide>}
          
          {!searching && !searchDone && <ParallaxSlide><SubscriptionSection /></ParallaxSlide>}
          {!searching && !searchDone && <ParallaxSlide><AboutSection /></ParallaxSlide>}

            <div ref={resultsRef} className={searching || searchDone ? 'max-w-6xl mx-auto px-4 sm:px-6 py-10' : ''}>
            {searching && <LoadingQuiz />}

            {!searching && searchDone && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    <Trans
                      i18nKey="jurisprudence.results_for"
                      count={allResults.length || results.length}
                      values={{ query }}
                      components={[<strong key="0" className="text-foreground" />, <em key="1" className="text-foreground" />]}
                    />
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </p>
                  <button onClick={handleReset} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t('search.new_search')}
                  </button>
                </div>
                <QuickFilters
                  results={allResults}
                  active={quickFilters}
                  onChange={setQuickFilters}
                />

                {(() => {
                  const hasFilters = Object.keys(quickFilters).length > 0;
                  const displayed  = hasFilters
                    ? applyQuickFilters(allResults, quickFilters)
                    : results;
                  return (
                <>
                {displayed.length === 0 ? (
                  <div className="text-center py-24 text-muted-foreground">
                    <p className="text-5xl mb-4">🔎</p>
                    <p className="font-semibold text-foreground mb-1">{t('jurisprudence.no_results_title')}</p>
                    <p className="text-sm">{t('jurisprudence.no_results_hint')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {displayed.map(f => <FicheCard key={f.id} fiche={f} showScore />)}
                    {!hasFilters && displayCount < allResults.length && (
                      <div className="flex justify-center pt-4">
                        <button onClick={loadMore}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-primary/40 hover:bg-accent text-sm font-medium transition-all">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {t('jurisprudence.load_more', { count: allResults.length - displayCount })}
                        </button>
                      </div>
                    )}
                    {!hasFilters && displayCount >= allResults.length && allResults.length > DISPLAY_STEP && (
                      <p className="text-center text-xs text-muted-foreground pt-4">
                        {t('jurisprudence.results_total', { count: allResults.length })}
                      </p>
                    )}
                  </div>
                )}
                </>
                );})()}
              </div>
            )}
            
          </div>
        </main>
        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}